import React, { useEffect, useState } from "react";
import DashboardLayout from "../layout/index";
import { toast } from "react-toastify";
import { Pagination } from "antd";
import { format } from "date-fns";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  User,
  Phone,
  Clock,
  Hash,
  CalendarCheck,
  Settings,
  UserCheck,
} from "lucide-react";

const PatientPendingList = () => {
  const API_URL = process.env.REACT_APP_NEW_DOCTOR_URL;

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState("");
  const [searchMobile, setSearchMobile] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState("pending");

  const [viewPatientDetails, setViewPatientDetails] = useState(null);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [rescheduleAppointment, setRescheduleAppointment] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [rescheduleSlots, setRescheduleSlots] = useState({
    morning: [],
    afternoon: [],
    evening: [],
  });
  const [rescheduleSession, setRescheduleSession] = useState("afternoon");
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const token = localStorage.getItem("auth_token");
  const tenantId = localStorage.getItem("tenantId");

  const today = new Date();
  const defaultStartOfMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  );

  // Date range picker state
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState({
    start: defaultStartOfMonth,
    end: today,
  });
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const formatDateLabel = (date) => {
    if (!date) return "";
    try {
      return format(date, "dd-MM-yyyy");
    } catch {
      return "";
    }
  };

  const formatSelectedDateLabel = () => {
    const { start, end } = selectedRange;
    if (start && end)
      return `${formatDateLabel(start)} — ${formatDateLabel(end)}`;
    if (start) return formatDateLabel(start);
    return "Date";
  };

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const handlePrevMonth = () => {
    setMonth((prev) => {
      if (prev === 0) {
        setYear((y) => y - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    setMonth((prev) => {
      if (prev === 11) {
        setYear((y) => y + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  const handleDayClick = (day) => {
    const current = new Date(year, month, day);
    const { start, end } = selectedRange;

    if (!start || (start && end)) {
      setSelectedRange({ start: current, end: null });
      return;
    }

    const startDateOnly = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate()
    );
    const currentDateOnly = new Date(
      current.getFullYear(),
      current.getMonth(),
      current.getDate()
    );

    let newRange;
    if (currentDateOnly < startDateOnly) {
      newRange = { start: current, end: startDateOnly };
    } else {
      newRange = { start: startDateOnly, end: currentDateOnly };
    }
    setSelectedRange(newRange);
    // Close picker after a complete range is selected
    setDatePickerOpen(false);
  };

  const fetchPendingPatients = async () => {
    try {
      setLoading(true);

      // Date range: from selected range if available, else current month (1st to today)
      let fromDateObj;
      let toDateObj;

      if (selectedRange.start && selectedRange.end) {
        fromDateObj = selectedRange.start;
        toDateObj = selectedRange.end;
      } else if (selectedRange.start) {
        fromDateObj = selectedRange.start;
        toDateObj = selectedRange.start;
      } else {
        const todayLocal = new Date();
        fromDateObj = new Date(
          todayLocal.getFullYear(),
          todayLocal.getMonth(),
          1
        );
        toDateObj = todayLocal;
      }

      // Use local dates to avoid timezone shifting (e.g. 08-03-2026 → 2026-03-08)
      const fromDate = format(fromDateObj, "yyyy-MM-dd");
      const toDate = format(toDateObj, "yyyy-MM-dd");

      const params = new URLSearchParams();
      params.set("fromDate", fromDate);
      params.set("toDate", toDate);
      params.set("status", statusFilter);
      params.set("page", String(currentPage));
      params.set("size", String(pageSize));
      params.set("paymentStatus", "SUCCESS");

      const trimmedName = searchName.trim();
      const trimmedMobile = searchMobile.trim();

      if (trimmedName) {
        // Assuming backend supports filtering by patient name
        params.set("patientName", trimmedName);
      }
      if (trimmedMobile) {
        params.set("mobileNumber", trimmedMobile);
      }

      const res = await fetch(
        `${API_URL}/appointments?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Authorization: `Bearer ${token}`,
            // tenantId: tenantId,
          },
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      console.log("datajhbjh", data);

      // API returns { data: [...], currentPage, pageSize, totalElements, ... }
      const rawItems = Array.isArray(data?.data) ? data.data : [];

      // Map appointment data to a simple patient-like shape for the table
      const mappedPatients = rawItems.map((item) => {
        const p = item.patient || item;
        return {
          id: p.id || item.id,
          firstName: p.firstName || p.fullName || "",
          lastName: p.lastName || "",
          age: p.age,
          mobileNumber: p.mobile || p.mobileNumber || p.phoneNumber,
          gender: p.gender,
          address: p.address,
          pinCode: p.pinCode,
          salutation: p.salutation,
          whenAppointment: item.whenAppointment,
          fromTime: item.fromTime,
          toTime: item.toTime,
          doctorCode: item.userId,
          consultationsType: item.consultationsType,
          doctorName: item.doctorFirstName + " " + item.doctorLastName,
        };
      });

      setPatients(mappedPatients);
      setTotalElements(
        Number(
          data?.totalElements ||
            data?.totalElementsCount ||
            rawItems?.length ||
            0
        )
      );
    } catch (error) {
      console.error("Failed to fetch pending patients:", error);
      toast.error("Failed to fetch pending patients");
      setPatients([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, selectedRange, statusFilter]);

  // Fetch available slots for reschedule modal
  const fetchRescheduleSlots = async (targetDate, appointmentOverride) => {
    console.log("uygiuhkjhjkhj", appointmentOverride);
    const appt = appointmentOverride || rescheduleAppointment;
    // if (!appt || !appt.doctorCode) return;

    try {
      setRescheduleLoading(true);
      const dateStr = format(targetDate, "yyyy-MM-dd");
      const doctorCode = appt.doctorCode;
      const consultationType = appt.consultationsType || "V";

      const url = `${API_URL}/slot/available?doctorCode=${encodeURIComponent(
        doctorCode
      )}&date=${dateStr}&consultationType=${encodeURIComponent(
        consultationType
      )}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      const slots = data?.slots || {};
      setRescheduleSlots({
        morning: slots.morning || [],
        afternoon: slots.afternoon || [],
        evening: slots.evening || [],
      });
    } catch (error) {
      console.error("Failed to fetch reschedule slots:", error);
      toast.error("Failed to load available slots");
      setRescheduleSlots({ morning: [], afternoon: [], evening: [] });
    } finally {
      setRescheduleLoading(false);
    }
  };

  // Save rescheduled appointment
  const handleSaveReschedule = async () => {
    if (!rescheduleAppointment || !selectedSlot) return;

    try {
      setRescheduleLoading(true);
      const [fromTime, toTime] = selectedSlot.split("-");
      const datePart = format(rescheduleDate, "yyyy-MM-dd");
      const whenAppointment = `${datePart}T${fromTime}:00`;

      const payload = {
        appointmentId: rescheduleAppointment.id,
        fromTime,
        toTime,
        whenAppointment,
        remark: "Patient requested reschedule",
      };
console.log("payloadggg", payload);
      const res = await fetch(`${API_URL}/appointments/reschedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP error! status: ${res.status}`);
      }

      toast.success("Appointment rescheduled successfully");
      setRescheduleModalOpen(false);
      setRescheduleAppointment(null);
      setSelectedSlot(null);
      fetchPendingPatients();
    } catch (error) {
      console.error("Failed to reschedule appointment:", error);
      toast.error(error.message || "Failed to reschedule appointment");
    } finally {
      setRescheduleLoading(false);
    }
  };

console.log("jhbkjbkjbkj", patients);
  return (
    <DashboardLayout>
      <div className="mt-8 px-6 max-w-[1800px] mx-auto">
        {/* Header + date picker + search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-50 p-2 rounded-xl border border-emerald-100 flex items-center justify-center">
              <UserCheck size={26} className="text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#334155] tracking-tight">
                Pending Appointments
              </h1>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {/* Status filter */}
            <div className="relative mobile:w-full">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-11 w-full sm:w-40 appearance-none rounded-xl border border-slate-200 bg-slate-50/50 pl-4 pr-10 text-sm text-slate-700 hover:bg-white hover:border-teal-500/50 focus:bg-white focus:border-teal-500/50 focus:outline-none transition-all"
              >
                <option value="pending">Pending</option>
                <option value="hold">Hold</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                <ChevronLeft size={16} className="-rotate-90" />
              </div>
            </div>

            {/* Date range picker */}
            <div className="relative mobile:w-full">
              <button
                type="button"
                onClick={() => {
                  setDatePickerOpen(!datePickerOpen);
                }}
                className="h-11 px-4 border border-slate-200 rounded-xl bg-slate-50/50 flex items-center gap-3 hover:bg-white hover:border-teal-500/50 transition-all justify-between text-sm text-slate-700 mobile:w-full mobile:justify-between mobile:text-sm"
              >
                <Calendar size={14} className="text-teal-600 mobile:w-4 mobile:h-4" />
                <span className="truncate">{formatSelectedDateLabel()}</span>
                <ChevronLeft
                  size={16}
                  className={`${datePickerOpen ? "rotate-90" : "-rotate-90"} transition-transform duration-200 mobile:w-4 mobile:h-4`}
                />
              </button>
              {datePickerOpen && (
                <div className="absolute right-0 top-[calc(100%+8px)] bg-white border border-slate-200 rounded-2xl shadow-2xl p-5 w-80 z-20 mobile:right-auto mobile:left-0 mobile:w-full mobile:max-w-[calc(100vw-2rem)] animate-in fade-in zoom-in-95 duration-200">
                  <div className="text-center mb-1">
                    <div className="flex items-center justify-between mb-5">
                      <button
                        type="button"
                        className="p-1.5 hover:bg-teal-50 rounded-lg transition-colors"
                        onClick={handlePrevMonth}
                      >
                        <ChevronLeft size={20} className="text-teal-600" />
                      </button>
                      <span className="text-slate-800">
                        {monthNames[month]} {year}
                      </span>
                      <button
                        type="button"
                        className="p-1.5 hover:bg-teal-50 rounded-lg transition-colors"
                        onClick={handleNextMonth}
                      >
                        <ChevronRight size={20} className="text-teal-600" />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                        (day) => (
                          <div
                            key={day}
                            className="text-[10px] uppercase tracking-widest font-black text-slate-300 p-2"
                          >
                            {day}
                          </div>
                        )
                      )}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {[...Array(firstDayOfMonth)].map((_, i) => (
                        <div key={`empty-${i}`} className="p-2" />
                      ))}
                      {[...Array(daysInMonth)].map((_, i) => {
                        const day = i + 1;
                        const { start, end } = selectedRange;
                        const current = new Date(year, month, day);
                        const isStart =
                          start &&
                          current.getTime() ===
                            new Date(
                              start.getFullYear(),
                              start.getMonth(),
                              start.getDate()
                            ).getTime();
                        const isEnd =
                          end &&
                          current.getTime() ===
                            new Date(
                              end.getFullYear(),
                              end.getMonth(),
                              end.getDate()
                            ).getTime();
                        const inRange =
                          start &&
                          end &&
                          current >=
                            new Date(
                              start.getFullYear(),
                              start.getMonth(),
                              start.getDate()
                            ) &&
                          current <=
                            new Date(
                              end.getFullYear(),
                              end.getMonth(),
                              end.getDate()
                            );

                        return (
                          <button
                            key={day}
                            onClick={() => handleDayClick(day)}
                            className={`h-9 w-9 text-xs rounded-lg ${
                              isStart || isEnd
                                ? "bg-teal-600 text-white font-black shadow-lg shadow-teal-200"
                                : inRange
                                ? "bg-teal-50 text-teal-700"
                                : "hover:bg-teal-50/50 text-slate-700"
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Search filters */}
            {/* <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Search by name"
                className="w-full sm:w-56 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#004276] focus:border-transparent"
              />
              <input
                type="tel"
                value={searchMobile}
                onChange={(e) =>
                  setSearchMobile(
                    e.target.value.replace(/\D/g, "").slice(0, 10)
                  )
                }
                placeholder="Search by mobile"
                className="w-full sm:w-56 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#004276] focus:border-transparent"
              />
            </div> */}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-sky-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Hash size={12} className="text-slate-400" />
                      Appointment No.
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-teal-600" />
                      Patient Details
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-teal-600" />
                    Doctor Name
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Clock size={12} className="text-slate-400" />
                      Appointment Time
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <CalendarCheck size={12} className="text-slate-400" />
                      Appointment Date
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-[#4B5563] tracking-widest border-b border-slate-100">
                    <div className="flex items-center justify-end gap-2">
                      <Settings size={12} className="text-slate-400" />
                      Action
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="relative h-12 w-12">
                          <div className="absolute inset-0 rounded-full border-4 border-teal-50 border-t-teal-600 animate-spin" />
                        </div>
                        <span className="text-sm font-black text-slate-400 uppercase tracking-widest animate-pulse">
                          Synchronizing Data...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : patients.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="h-16 w-16 rounded-3xl bg-slate-50 flex items-center justify-center">
                          <User size={32} className="text-slate-200" />
                        </div>
                        <span className="text-sm font-semibold text-slate-400">
                          No pending patients found
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  patients.map((patient) => (
                    <tr key={patient.id} className="group transition-all hover:bg-teal-50/20">
                      <td className="px-5 py-5 whitespace-nowrap">
                        <span className="text-sm text-slate-800 tracking-tight">
                          {patient.id ?? "-"}
                        </span>
                      </td>
                      <td className="px-5 py-5 min-w-[280px]">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2.5">
                            <div className="h-5 w-5 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center flex-shrink-0 shadow-sm">
                              <User size={13} className="text-teal-600" />
                            </div>
                            <span className="text-sm text-slate-900 leading-tight">
                              {patient.firstName} {patient.lastName}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-1.5 ml-7">
                            <Phone size={12} className="text-teal-600/70" />
                            <span className="text-sm text-slate-500">
                              {patient.mobileNumber ?? "-"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-slate-500">
                          <span className="text-sm text-slate-800  tracking-wider">
                            {patient.doctorName}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-2 bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 w-fit">
                          <Clock size={12} className="text-slate-400" />
                          <span className="text-sm text-slate-600 tracking-tighter">
                            {patient.fromTime ? `${patient.fromTime}${patient.toTime ? ` - ${patient.toTime}` : ""}` : "--:--"}
                          </span>
                        </div>
                      </td>
                      
                      <td className="px-5 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-slate-500">
                          <CalendarCheck size={13} />
                          <span className="text-sm text-slate-800 uppercase tracking-wider">
                            {patient.whenAppointment
                              ? format(new Date(patient.whenAppointment), "dd-MM-yyyy")
                              : "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-5 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              setRescheduleAppointment(patient);
                              const baseDate = new Date();
                              setRescheduleDate(baseDate);
                              setSelectedSlot(null);
                              setRescheduleSession("afternoon");
                              setRescheduleModalOpen(true);
                              fetchRescheduleSlots(baseDate, patient);
                            }}
                            className="h-9 px-3 flex items-center justify-center rounded-xl text-white bg-teal-600 hover:bg-teal-700 transition-all shadow-sm border border-teal-600 text-sm"
                          >
                            Reschedule
                          </button>
                          {/* <button
                            type="button"
                            disabled
                            aria-disabled="true"
                            className="h-9 px-3 rounded-xl text-rose-300 bg-white border border-rose-100 text-sm cursor-not-allowed opacity-70"
                          >
                            Cancel
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        {/* Reschedule Modal */}
        {rescheduleModalOpen && rescheduleAppointment && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-2xl w-11/12 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-[#111827]">
                  Reschedule Appointment
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setRescheduleModalOpen(false);
                    setRescheduleAppointment(null);
                    setSelectedSlot(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close reschedule"
                >
                  <span className="text-2xl leading-none">&times;</span>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Date picker for reschedule */}
                <div>
                  <h3 className="text-sm font-semibold text-[#004276] mb-3">
                    Select Date
                  </h3>
                  <div className="inline-block bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                    <div className="flex items-center justify-between mb-3">
                      <button
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={handlePrevMonth}
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span className="font-semibold">
                        {monthNames[month]} {year}
                      </span>
                      <button
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={handleNextMonth}
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                        (day) => (
                          <div
                            key={day}
                            className="text-xs font-medium text-gray-600 p-2 text-center"
                          >
                            {day}
                          </div>
                        )
                      )}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {[...Array(firstDayOfMonth)].map((_, i) => (
                        <div key={`empty-res-${i}`} className="p-2" />
                      ))}
                      {[...Array(daysInMonth)].map((_, i) => {
                        const day = i + 1;
                        const current = new Date(year, month, day);

                        // Disable past dates
                        const todayOnly = new Date();
                        todayOnly.setHours(0, 0, 0, 0);
                        const currentOnly = new Date(
                          current.getFullYear(),
                          current.getMonth(),
                          current.getDate()
                        );
                        const isPast = currentOnly < todayOnly;

                        const isSelected =
                          format(current, "yyyy-MM-dd") ===
                          format(rescheduleDate, "yyyy-MM-dd");

                        return (
                          <button
                            key={day}
                            type="button"
                            disabled={isPast}
                            onClick={() => {
                              if (isPast) return;
                              setRescheduleDate(current);
                              setSelectedSlot(null);
                              fetchRescheduleSlots(current, rescheduleAppointment);
                            }}
                            className={`p-2 text-sm rounded ${
                              isPast
                                ? "text-gray-300 cursor-not-allowed"
                                : isSelected
                                ? "bg-green-500 text-white font-semibold"
                                : "hover:bg-gray-100"
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Slots */}
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-[#004276]">
                      Available Slots
                    </h3>
                    <span className="text-xs text-gray-500">
                      {format(rescheduleDate, "dd-MM-yyyy")}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    {["morning", "afternoon", "evening"].map((session) => {
                      const count = rescheduleSlots[session]?.length || 0;
                      const label =
                        session.charAt(0).toUpperCase() + session.slice(1);
                      return (
                        <button
                          key={session}
                          type="button"
                          onClick={() => setRescheduleSession(session)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                            rescheduleSession === session
                              ? "bg-[#004276] text-white border-[#004276]"
                              : "bg-white text-gray-700 border-gray-300"
                          }`}
                        >
                          {label}{" "}
                          <span className="ml-1 text-[10px] text-gray-300">
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex-1 overflow-y-auto border border-gray-100 rounded-xl p-3">
                    {rescheduleLoading ? (
                      <div className="text-sm text-gray-500">
                        Loading slots...
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {(rescheduleSlots[rescheduleSession] || [])
                          .filter((s) => s.available === "Y")
                          .map((slotObj) => {
                            const label = slotObj.slot;
                            const isSelected = selectedSlot === label;
                            return (
                              <button
                                key={label}
                                type="button"
                                onClick={() => setSelectedSlot(label)}
                                className={`px-3 py-2 rounded-lg text-xs sm:text-sm border ${
                                  isSelected
                                    ? "bg-[#004276] text-white border-[#004276]"
                                    : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
                                }`}
                              >
                                {label.split("-")[0]}
                              </button>
                            );
                          })}
                        {(!rescheduleSlots[rescheduleSession] ||
                          rescheduleSlots[rescheduleSession].filter(
                            (s) => s.available === "Y"
                          ).length === 0) &&
                          !rescheduleLoading && (
                            <div className="col-span-full text-sm text-gray-500">
                              No slots available in this period.
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setRescheduleModalOpen(false);
                    setRescheduleAppointment(null);
                    setSelectedSlot(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  type="button"
                  disabled={!selectedSlot || rescheduleLoading}
                  className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 ${
                    selectedSlot && !rescheduleLoading
                      ? "bg-[#004276] text-white hover:bg-[#003060]"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  }`}
                  onClick={handleSaveReschedule}
                >
                  {rescheduleLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
          <div className="text-sm text-slate-600">
            {typeof totalElements === "number" ? `Total: ${totalElements}` : ""}
          </div>
          <Pagination
            current={currentPage}
            total={totalElements || patients.length}
            pageSize={pageSize}
            showSizeChanger
            pageSizeOptions={[10, 20, 50]}
            onChange={(page, size) => {
              setCurrentPage(page || 1);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            }}
            onShowSizeChange={(_, size) => {
              setPageSize(size || 20);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* Sidebar for details */}
        {viewPatientDetails && (
          <div
            className="fixed inset-0 z-40 flex justify-end bg-black/40"
            onClick={() => setViewPatientDetails(null)}
          >
            <div
              className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top close icon */}
              <div className="flex items-center justify-end px-6 py-4 border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => setViewPatientDetails(null)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close details"
                >
                  <span className="text-2xl leading-none">&times;</span>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                <h2 className="text-lg font-semibold text-[#111827]">
                  Patient Details
                </h2>

                <div className="rounded-xl bg-[#F5F7FF] px-4 py-4 text-sm text-gray-700 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8">
                    <div>
                      <p className="font-medium text-gray-500">Name</p>
                      <p className="text-gray-800">
                        {[
                          viewPatientDetails.salutation,
                          viewPatientDetails.firstName,
                          viewPatientDetails.middleName,
                          viewPatientDetails.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ") || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-500">Mobile Number</p>
                      <p>{viewPatientDetails.mobileNumber || "-"}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-500">Age</p>
                      <p>{viewPatientDetails.age ?? "-"}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-500">Gender</p>
                      <p>{viewPatientDetails.gender || "-"}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="font-medium text-gray-500">Address</p>
                      <p>{viewPatientDetails.address || "-"}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-500">Pin Code</p>
                      <p>{viewPatientDetails.pinCode || "-"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom close button */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  type="button"
                  onClick={() => setViewPatientDetails(null)}
                  className="px-4 py-2 text-sm font-medium text-[#004276] border border-[#004276] rounded-lg hover:bg-[#004276] hover:text-white transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PatientPendingList;

